import React, { useState, useEffect, useCallback } from 'react';
import { adsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Image as ImageIcon, Plus, Trash2, X, Upload, Eye, EyeOff, HardDrive, Check, Cloud } from 'lucide-react';
import { pickOrUploadDriveImage, isGoogleDriveConfigured } from '../components/common/googleDrive';
import useDarkReady from '../theme/useDarkReady';

// Picks from image files already sitting in uploads/ads/ on the server (past
// uploads, including ones no longer attached to any current ad) instead of
// uploading the same file again.
function ServerImagePicker({ selected, onSelect }) {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adsAPI.getServerImages()
      .then(({ data }) => setImages(data.data || []))
      .catch(() => toast.error('Failed to load server images'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto', width: 22, height: 22, borderWidth: 2 }} /></div>;

  if (images.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-muted)', fontSize: '.85rem' }}>
        <HardDrive size={22} style={{ marginBottom: 6 }} />
        <div>No images on the server yet — upload one first.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8, maxHeight: 260, overflowY: 'auto', padding: 2 }}>
      {images.map(img => {
        const isSelected = selected?.filename === img.filename;
        return (
          <div
            key={img.filename}
            onClick={() => onSelect(img)}
            style={{
              position: 'relative', aspectRatio: '1/1', borderRadius: 8, overflow: 'hidden',
              cursor: 'pointer', border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
              background: 'var(--cb-f9fafb)',
            }}
          >
            <img src={img.url} alt={img.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {isSelected && (
              <div style={{
                position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%',
                background: 'var(--primary)', color: 'var(--c-ffffff)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={13} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Upload modal — image + optional title/link, mirrors the pattern used by
// DevicesPage's AddDeviceModal (modal-overlay/modal shell + form). Supports
// either uploading a new file or picking one already sitting on the server.
function UploadAdModal({ onClose, onSuccess }) {
  const [mode, setMode]       = useState('upload'); // 'upload' | 'server' | 'drive'
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [serverImage, setServerImage] = useState(null);
  const [driveImage, setDriveImage] = useState(null); // { fileId, name, imageUrl }
  const [drivePicking, setDrivePicking] = useState(false);
  const [title, setTitle]     = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const switchMode = (m) => {
    setMode(m);
    setFile(null); setPreview(null); setServerImage(null); setDriveImage(null);
  };

  const handlePickFromDrive = async () => {
    setDrivePicking(true);
    try {
      const picked = await pickOrUploadDriveImage();
      setDriveImage(picked);
    } catch (err) {
      if (err.message !== 'cancelled') toast.error(err.message || 'Google Drive picker failed');
    } finally { setDrivePicking(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (mode === 'upload') {
        if (!file) { toast.error('Choose an image to upload'); setUploading(false); return; }
        const formData = new FormData();
        formData.append('image', file);
        if (title) formData.append('title', title);
        if (linkUrl) formData.append('linkUrl', linkUrl);
        await adsAPI.upload(formData);
        toast.success('Ad uploaded');
      } else if (mode === 'server') {
        if (!serverImage) { toast.error('Choose an image from the server'); setUploading(false); return; }
        await adsAPI.createFromServerImage({ imageUrl: serverImage.url, title, linkUrl });
        toast.success('Ad created');
      } else {
        if (!driveImage) { toast.error('Pick or upload an image via Google Drive first'); setUploading(false); return; }
        await adsAPI.createFromServerImage({ imageUrl: driveImage.imageUrl, title, linkUrl, source: 'drive' });
        toast.success('Ad created');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setUploading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Add Ad Image</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <button type="button" onClick={() => switchMode('upload')}
                style={{
                  flex: 1, padding: '8px 0', fontSize: '.82rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: mode === 'upload' ? 'var(--primary)' : 'var(--cb-f9fafb)', color: mode === 'upload' ? 'var(--c-ffffff)' : 'var(--text-muted)',
                }}>
                <Upload size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />Upload New
              </button>
              <button type="button" onClick={() => switchMode('server')}
                style={{
                  flex: 1, padding: '8px 0', fontSize: '.82rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: mode === 'server' ? 'var(--primary)' : 'var(--cb-f9fafb)', color: mode === 'server' ? 'var(--c-ffffff)' : 'var(--text-muted)',
                }}>
                <HardDrive size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />Choose from Server
              </button>
              <button type="button" onClick={() => switchMode('drive')}
                style={{
                  flex: 1, padding: '8px 0', fontSize: '.82rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: mode === 'drive' ? 'var(--primary)' : 'var(--cb-f9fafb)', color: mode === 'drive' ? 'var(--c-ffffff)' : 'var(--text-muted)',
                }}>
                <Cloud size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />Google Drive
              </button>
            </div>

            {/* The customer-app banner (AdBanner.js) crops to a 16:7 frame with object-fit: cover */}
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 10 }}>
              Recommended size: <strong>1600 × 700 px</strong> (16:7). Other sizes are cropped to fit the banner.
            </div>

            {mode === 'upload' ? (
              <div className="form-group">
                <label className="form-label">Image <span className="required">*</span></label>
                {preview ? (
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <img src={preview} alt="preview" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--cb-f9fafb)' }} />
                  </div>
                ) : (
                  <label style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    padding: '28px 16px', border: '1.5px dashed var(--border)', borderRadius: 8,
                    cursor: 'pointer', color: 'var(--text-muted)', background: 'var(--cb-f9fafb)',
                  }}>
                    <Upload size={22} />
                    <span style={{ fontSize: '.85rem' }}>Click to choose an image (max 5MB)</span>
                    <span style={{ fontSize: '.75rem' }}>Best size: 1600 × 700 px</span>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
                  </label>
                )}
                {preview && (
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                    Replace Image
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            ) : mode === 'server' ? (
              <div className="form-group">
                <label className="form-label">Server Images <span className="required">*</span></label>
                <ServerImagePicker selected={serverImage} onSelect={setServerImage} />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Google Drive <span className="required">*</span></label>
                {!isGoogleDriveConfigured() ? (
                  <div style={{ textAlign: 'center', padding: '20px 12px', color: 'var(--text-muted)', fontSize: '.85rem', border: '1px dashed var(--border)', borderRadius: 8, background: 'var(--cb-f9fafb)' }}>
                    Google Drive isn't configured yet — an admin needs to set REACT_APP_GOOGLE_CLIENT_ID / REACT_APP_GOOGLE_API_KEY.
                  </div>
                ) : driveImage ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--cb-f9fafb)' }}>
                    <img src={driveImage.imageUrl} alt={driveImage.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{driveImage.name}</div>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={handlePickFromDrive} disabled={drivePicking}>
                        Choose Different Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={handlePickFromDrive} disabled={drivePicking}>
                    <Cloud size={14} /> {drivePicking ? 'Opening Google Drive…' : 'Pick or Upload via Google Drive'}
                  </button>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Title <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 12-Month Offer" />
            </div>
            <div className="form-group">
              <label className="form-label">Link URL <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Opens when the customer taps the ad" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              <Upload size={14} /> {uploading ? 'Saving…' : mode === 'upload' ? 'Upload' : 'Add Ad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdsPage() {
  useDarkReady();
  const [ads, setAds]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [busyId, setBusyId]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adsAPI.getAll();
      setAds(data.data || []);
    } catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (ad) => {
    setBusyId(ad._id);
    try {
      await adsAPI.update(ad._id, { isActive: !ad.isActive });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setBusyId(null); }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm('Delete this ad image?')) return;
    setBusyId(ad._id);
    try {
      await adsAPI.delete(ad._id);
      toast.success('Ad deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>App Advertisement — Customer App Banner</h2>
          <p className="text-muted text-small">{ads.length} image{ads.length !== 1 ? 's' : ''} · shown on the customer app dashboard after login</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
          <Plus size={15} style={{ marginRight: 5 }} />Add Image
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : ads.length === 0 ? (
        <div className="card empty-state" style={{ padding: 48 }}>
          <ImageIcon size={32} />
          <h3>No ads uploaded yet</h3>
          <p>Upload promotional images to show them on the customer app's dashboard.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {ads.map(ad => (
            <div key={ad._id} className="card" style={{ opacity: ad.isActive ? 1 : .55 }}>
              <div style={{ aspectRatio: '16/9', background: 'var(--cb-f3f4f6)', overflow: 'hidden' }}>
                <img src={ad.imageUrl} alt={ad.title || 'ad'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '10px 12px' }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 6, minHeight: 18 }}>{ad.title || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Untitled</span>}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} disabled={busyId === ad._id} onClick={() => toggleActive(ad)}>
                    {ad.isActive ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={busyId === ad._id} onClick={() => handleDelete(ad)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadAdModal onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}
